<template>
	<v-container fluid class="pa-0">
		<v-card class="mb-offset" outlined>
			<v-card-title>
				<v-icon left>mdi-database-import</v-icon>
				Import Dati HR — Migrazione da Dipendenti in Cloud
				<v-spacer />
				<v-btn small text color="primary" @click="resetWizard()" v-if="step > 1">
					<v-icon left small>mdi-restart</v-icon>Ricomincia
				</v-btn>
			</v-card-title>

			<v-card-subtitle>
				Procedura guidata per importare anagrafica, contratti e saldi ferie
				dal tuo precedente gestionale. Esegui gli import nell'ordine: 1)
				Anagrafica, 2) Contratti, 3) Saldi.
			</v-card-subtitle>

			<v-tabs v-model="activeTab" grow>
				<v-tab>Nuovo import</v-tab>
				<v-tab>Storico</v-tab>
			</v-tabs>

			<v-tabs-items v-model="activeTab">
				<v-tab-item>
					<v-stepper v-model="step" vertical class="elevation-0">

						<!-- ============== STEP 1: TIPO + UPLOAD ============== -->
						<v-stepper-step :complete="step > 1" step="1">
							Tipo di import e caricamento file
							<small v-if="kind">{{ kindLabel(kind) }} — {{ fileName || 'nessun file' }}</small>
						</v-stepper-step>

						<v-stepper-content step="1">
							<v-card flat>
								<v-card-text>
									<div class="text-subtitle-2 mb-2">Cosa vuoi importare?</div>
									<v-radio-group v-model="kind" :disabled="!!importId" class="mt-0">
										<v-radio label="1. Anagrafica dipendenti" value="employees" />
										<v-radio label="2. Contratti (richiede anagrafica già importata)" value="contracts" />
										<v-radio label="3. Saldi ferie/permessi al go-live" value="leave_balances" />
									</v-radio-group>

									<v-divider class="my-3" />

									<div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
										<v-btn
											small
											outlined
											color="primary"
											:disabled="!kind"
											:loading="loadingTemplate"
											@click="downloadTemplate()"
										>
											<v-icon left small>mdi-file-excel-outline</v-icon>
											Scarica modello XLSX
										</v-btn>
										<span class="caption grey--text">
											Non parti da un export di Dipendenti in Cloud? Scarica il modello
											già intestato, compilalo e ricaricalo qui: le colonne verranno
											riconosciute in automatico.
										</span>
									</div>

									<div class="text-subtitle-2 mb-2">File da caricare (XLSX, XLS o CSV)</div>
									<v-file-input
										v-model="uploadFile"
										accept=".xlsx,.xls,.csv"
										label="Seleziona file di export da Dipendenti in Cloud"
										prepend-icon="mdi-paperclip"
										:disabled="!kind"
										show-size
									/>
									<v-alert dense type="info" outlined v-if="kind === 'contracts'">
										Per i contratti, importa prima l'anagrafica dipendenti — il match
										avviene per Codice Fiscale.
									</v-alert>
								</v-card-text>

								<v-card-actions>
									<v-spacer />
									<v-btn
										color="primary"
										:disabled="!kind || !uploadFile"
										:loading="loadingUpload"
										@click="startUpload()"
									>
										Carica e prosegui
										<v-icon right>mdi-arrow-right</v-icon>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-stepper-content>

						<!-- ============== STEP 2: MAPPATURA COLONNE ============== -->
						<v-stepper-step :complete="step > 2" step="2">
							Mappatura colonne
							<small v-if="targetFields.length">{{ Object.keys(mapping).length }} / {{ targetFields.length }} campi mappati</small>
						</v-stepper-step>

						<v-stepper-content step="2">
							<v-card flat>
								<v-card-text>
									<v-alert type="info" dense outlined>
										La mappatura predefinita riconosce gli header standard di Dipendenti
										in Cloud e quelli del modello scaricabile. Modifica solo se il tuo
										file ha nomi colonna diversi.
									</v-alert>

									<v-simple-table dense>
										<template v-slot:default>
											<thead>
												<tr>
													<th>Campo destinazione</th>
													<th>Colonna nel file</th>
													<th>Esempio</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="field in targetFields" :key="field.key">
													<td>
														<strong>{{ field.label }}</strong>
														<span v-if="field.required" class="red--text">*</span>
														<div class="caption grey--text" v-if="field.note">{{ field.note }}</div>
													</td>
													<td style="min-width: 240px">
														<v-autocomplete
															v-model="mapping[field.key]"
															:items="headerOptions"
															clearable
															dense
															hide-details
															placeholder="(non mappare)"
														/>
													</td>
													<td class="caption">
														{{ sampleFor(mapping[field.key]) }}
													</td>
												</tr>
											</tbody>
										</template>
									</v-simple-table>

									<div class="caption mt-2">
										File: <strong>{{ fileName }}</strong> — {{ totalRows }} righe rilevate
									</div>
								</v-card-text>

								<v-card-actions>
									<v-btn text @click="step = 1">Indietro</v-btn>
									<v-spacer />
									<v-btn color="primary" :loading="loadingDryRun" @click="runDryRun()">
										Esegui anteprima
										<v-icon right>mdi-eye-outline</v-icon>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-stepper-content>

						<!-- ============== STEP 3: PREVIEW (DRY-RUN) ============== -->
						<v-stepper-step :complete="step > 3" step="3">
							Anteprima
							<small v-if="importModel.id">
								{{ importModel.rows_created }} nuove ·
								{{ importModel.rows_updated }} aggiornate ·
								{{ importModel.rows_skipped }} skip ·
								{{ importModel.rows_failed }} errori
							</small>
						</v-stepper-step>

						<v-stepper-content step="3">
							<v-card flat>
								<v-card-text>
									<v-alert v-if="formatMessage" type="info" outlined dense class="mb-2">
										{{ formatMessage }}
									</v-alert>
									<v-row dense class="mb-2">
										<v-col cols="auto">
											<v-chip color="success" small>
												<v-icon left small>mdi-plus-circle</v-icon>
												{{ importModel.rows_created }} nuove
											</v-chip>
										</v-col>
										<v-col cols="auto">
											<v-chip color="primary" small>
												<v-icon left small>mdi-update</v-icon>
												{{ importModel.rows_updated }} aggiornate
											</v-chip>
										</v-col>
										<v-col cols="auto">
											<v-chip color="grey" small>
												<v-icon left small>mdi-skip-next</v-icon>
												{{ importModel.rows_skipped }} skip
											</v-chip>
										</v-col>
										<v-col cols="auto">
											<v-chip color="error" small v-if="importModel.rows_failed">
												<v-icon left small>mdi-alert</v-icon>
												{{ importModel.rows_failed }} errori
											</v-chip>
										</v-col>
									</v-row>

									<!-- Risoluzione CCNL non riconosciuti (solo kind=contracts) -->
									<div v-if="kind === 'contracts' && unresolvedCcnls.length" class="mb-3">
										<v-alert type="warning" outlined dense>
											<strong>{{ unresolvedCcnls.length }} CCNL non riconosciuti.</strong>
											Scegli per ogni testo il CCNL canonico, oppure lascialo come
											testo libero.
										</v-alert>
										<v-card outlined class="mb-2" v-for="alias in unresolvedCcnls" :key="alias.text">
											<v-card-text class="py-2">
												<v-row align="center" dense>
													<v-col cols="12" md="4">
														<div class="caption grey--text">Testo nel file</div>
														<strong>{{ alias.text }}</strong>
														<div class="caption">{{ alias.count }} riga/righe</div>
													</v-col>
													<v-col cols="12" md="6">
														<v-autocomplete
															v-model="alias.selectedCcnlId"
															:items="ccnls"
															item-text="name"
															item-value="id"
															label="CCNL canonico"
															clearable
															hide-details
															dense
														>
															<template v-slot:append-outer>
																<v-btn icon small @click="openCreateCcnlDialog(alias)" title="Crea nuovo CCNL">
																	<v-icon small>mdi-plus</v-icon>
																</v-btn>
															</template>
														</v-autocomplete>
														<v-row dense v-if="alias.candidates && alias.candidates.length" class="mt-1">
															<v-col cols="12">
																<div class="caption grey--text">Suggerimenti:</div>
																<v-chip
																	v-for="c in alias.candidates"
																	:key="c.ccnl_id"
																	x-small
																	class="mr-1"
																	@click="alias.selectedCcnlId = c.ccnl_id"
																>
																	{{ c.name }} ({{ c.confidence }}%)
																</v-chip>
															</v-col>
														</v-row>
													</v-col>
													<v-col cols="12" md="2" class="text-right">
														<v-btn small color="primary" @click="resolveCcnl(alias, false)" :disabled="!alias.selectedCcnlId">
															Conferma
														</v-btn>
														<div>
															<v-btn x-small text @click="resolveCcnl(alias, true)">
																Lascia free text
															</v-btn>
														</div>
													</v-col>
												</v-row>
											</v-card-text>
										</v-card>
									</div>

									<!-- Tabella preview -->
									<v-data-table
										:headers="previewHeaders"
										:items="previewRows"
										:items-per-page="20"
										dense
										class="mb-3"
									>
										<template v-slot:item.action="{ item }">
											<v-chip x-small :color="actionColor(item.action)" dark>
												{{ actionLabel(item.action) }}
											</v-chip>
										</template>
										<template v-slot:item.summary="{ item }">
											<div v-if="item.error" class="error--text caption">{{ item.error }}</div>
											<div v-else-if="item.reason" class="grey--text caption">{{ item.reason }}</div>
											<div v-else-if="item.changes" class="caption">
												<span v-for="key in Object.keys(item.changes)" :key="key" class="mr-2">
													<strong>{{ key }}</strong>:
													<s>{{ item.changes[key].old || '∅' }}</s> →
													{{ item.changes[key].new }}
												</span>
											</div>
											<div v-else class="caption">{{ rowSummary(item) }}</div>
										</template>
									</v-data-table>
								</v-card-text>

								<v-card-actions>
									<v-btn text @click="step = 2">Indietro</v-btn>
									<v-spacer />
									<v-btn
										color="success"
										:loading="loadingCommit"
										:disabled="kind === 'contracts' && unresolvedCcnls.length > 0"
										@click="runCommit()"
									>
										Applica modifiche
										<v-icon right>mdi-check-bold</v-icon>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-stepper-content>

						<!-- ============== STEP 4: COMPLETATO ============== -->
						<v-stepper-step step="4" :complete="step >= 4">
							Completato
						</v-stepper-step>

						<v-stepper-content step="4">
							<v-card flat>
								<v-card-text>
									<v-alert type="success" outlined>
										Import completato con successo.
										{{ importModel.rows_created }} record creati,
										{{ importModel.rows_updated }} aggiornati.
									</v-alert>

									<div class="mt-3">
										<strong>Prossimo passo consigliato:</strong>
										<ul>
											<li v-if="kind === 'employees'">Importa ora i <strong>contratti</strong> dei dipendenti.</li>
											<li v-if="kind === 'contracts'">Importa ora i <strong>saldi ferie/permessi</strong> al go-live.</li>
											<li v-if="kind === 'leave_balances'">Migrazione completata. Verifica il <a href="/hremployees">listato dipendenti</a>.</li>
										</ul>
									</div>
								</v-card-text>

								<v-card-actions>
									<v-btn color="primary" @click="resetWizard()">Nuovo import</v-btn>
									<v-spacer />
									<v-btn text @click="activeTab = 1">Vedi storico</v-btn>
								</v-card-actions>
							</v-card>
						</v-stepper-content>
					</v-stepper>
				</v-tab-item>

				<!-- ============== TAB 2: STORICO ============== -->
				<v-tab-item>
					<v-card flat>
						<v-card-text>
							<v-data-table
								:headers="historyHeaders"
								:items="history"
								:loading="loadingHistory"
								dense
							>
								<template v-slot:item.kind="{ item }">{{ kindLabel(item.kind) }}</template>
								<template v-slot:item.status="{ item }">
									<v-chip x-small :color="statusColor(item.status)" dark>
										{{ statusLabelOf(item.status) }}
									</v-chip>
								</template>
								<template v-slot:item.created_at="{ item }">
									{{ formatDate(item.created_at) }}
								</template>
							</v-data-table>
						</v-card-text>
					</v-card>
				</v-tab-item>
			</v-tabs-items>
		</v-card>

		<!-- Dialog crea nuovo CCNL inline -->
		<v-dialog v-model="newCcnlDialog" max-width="500">
			<v-card>
				<v-card-title>Crea nuovo CCNL</v-card-title>
				<v-card-text>
					<v-text-field v-model="newCcnl.name" label="Denominazione" autofocus required />
					<v-text-field v-model="newCcnl.code" label="Codice CNEL (opzionale)" />
					<v-text-field v-model="newCcnl.sector" label="Settore (opzionale)" />
				</v-card-text>
				<v-card-actions>
					<v-btn text @click="newCcnlDialog = false">Annulla</v-btn>
					<v-spacer />
					<v-btn color="primary" :disabled="!newCcnl.name" @click="confirmCreateCcnl()">Crea</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
export default {
	name: 'HrImports',

	data() {
		return {
			activeTab: 0,
			step: 1,

			kind: null,
			uploadFile: null,
			fileName: null,
			importId: null,
			importModel: {},

			headers: [],
			sampleRows: [],
			totalRows: 0,
			targetFields: [],
			mapping: {},

			ccnls: [],
			newCcnlDialog: false,
			newCcnl: { name: '', code: '', sector: '' },
			newCcnlPendingAlias: null,

			// Formato file rilevato dal backend ('tabular' | 'dic_bilancio_multi_sheet')
			detectedFormat: 'tabular',
			formatMessage: null,

			loadingUpload: false,
			loadingTemplate: false,
			loadingDryRun: false,
			loadingCommit: false,
			loadingHistory: false,

			history: [],

			historyHeaders: [
				{ text: 'Tipo', value: 'kind' },
				{ text: 'Sorgente', value: 'source' },
				{ text: 'Stato', value: 'status' },
				{ text: 'Righe', value: 'rows_total' },
				{ text: 'Create', value: 'rows_created' },
				{ text: 'Aggiornate', value: 'rows_updated' },
				{ text: 'Errori', value: 'rows_failed' },
				{ text: 'Data', value: 'created_at' },
			],
		}
	},

	computed: {
		headerOptions() {
			return this.headers.map(h => ({ text: h, value: h }))
		},
		previewHeaders() {
			const base = [
				{ text: 'Riga', value: 'row', width: 60 },
				{ text: 'Azione', value: 'action', width: 130 },
			]
			if (this.detectedFormat === 'dic_bilancio_multi_sheet') {
				base.push({ text: 'Sheet', value: 'sheet' })
			}
			if (this.kind === 'contracts' || this.kind === 'leave_balances') {
				base.push({ text: 'Dipendente', value: 'employee_name' })
			}
			base.push({ text: 'Dettaglio', value: 'summary' })
			return base
		},
		previewRows() {
			return this.importModel.preview || []
		},
		/**
		 * Aggrega gli alias CCNL non riconosciuti dalle righe del preview,
		 * con count e candidati.
		 */
		unresolvedCcnls() {
			if (this.kind !== 'contracts') return []
			const map = {}
			for (const row of this.previewRows) {
				if (row.action === 'failed') continue
				if (!row.ccnl_match) continue
				if (row.ccnl_match.source === 'none' || (row.ccnl_match.source === 'fuzzy' && !row.ccnl_match.ccnl_id)) {
					const text = row.data && row.data.ccnl_code
					if (!text) continue
					if (!map[text]) {
						map[text] = {
							text,
							count: 0,
							candidates: row.ccnl_match.candidates || [],
							selectedCcnlId: null,
						}
					}
					map[text].count++
				}
			}
			return Object.values(map)
		},
	},

	mounted() {
		this.loadHistory()
	},

	methods: {
		kindLabel(k) {
			return { employees: 'Anagrafica dipendenti', contracts: 'Contratti', leave_balances: 'Saldi ferie/permessi' }[k] || k
		},
		statusLabelOf(s) {
			return { pending: 'In attesa', dry_run: 'Preview pronto', committed: 'Completato', failed: 'Errore' }[s] || s
		},
		statusColor(s) {
			return { pending: 'grey', dry_run: 'orange', committed: 'success', failed: 'error' }[s] || 'grey'
		},
		actionColor(a) {
			return { created: 'success', updated: 'primary', skipped: 'grey', failed: 'error' }[a] || 'grey'
		},
		actionLabel(a) {
			return { created: 'Nuovo', updated: 'Aggiorna', skipped: 'Skip', failed: 'Errore' }[a] || a
		},
		formatDate(d) {
			if (!d) return ''
			return new Date(d).toLocaleString('it-IT')
		},
		sampleFor(header) {
			if (!header || !this.sampleRows.length) return ''
			const v = this.sampleRows[0][header]
			return v !== undefined && v !== null ? String(v).substring(0, 50) : ''
		},
		rowSummary(item) {
			if (!item.data) return ''
			const d = item.data
			if (this.kind === 'employees') return `${d.first_name || ''} ${d.last_name || ''} (${d.tax_code || '—'})`
			if (this.kind === 'contracts') return `${d.type || ''} dal ${d.start_date || ''}`
			if (this.kind === 'leave_balances') return `${item.absence_type_name || ''} ${d.year || ''}: ${d.residual_hours}h residue`
			return ''
		},

		/**
		 * Scarica il modello XLSX vuoto per il tipo di import selezionato. Le
		 * intestazioni corrispondono al mapping di default del backend, quindi
		 * un file compilato sul modello arriva allo step 2 gia' tutto abbinato.
		 */
		async downloadTemplate() {
			if (!this.kind) return
			this.loadingTemplate = true
			try {
				const r = await this.axios.get(`hrimports/template/${this.kind}`, { responseType: 'blob' })
				const blob = new Blob([r.data], {
					type: r.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
				this.$eventBusUtility.showFile(blob, this.templateFileName())
			} catch (e) {
				this.handleError(e, 'Errore scaricamento modello')
			} finally {
				this.loadingTemplate = false
			}
		},

		/**
		 * Nome con cui salvare il modello: il Content-Disposition non e'
		 * leggibile lato browser senza esporre l'header via CORS.
		 */
		templateFileName() {
			return {
				employees: 'modello-anagrafica-dipendenti.xlsx',
				contracts: 'modello-contratti.xlsx',
				leave_balances: 'modello-saldi-ferie-permessi.xlsx',
			}[this.kind] || 'modello-import.xlsx'
		},

		async startUpload() {
			this.loadingUpload = true
			try {
				// 1. Crea il record import
				const createR = await this.axios.post('hrimports', { kind: this.kind, source: 'dic' })
				this.importId = createR.data.id

				// 2. Upload del file
				const fd = new FormData()
				fd.append('file', this.uploadFile)
				await this.axios.post(`hrimports/${this.importId}/upload`, fd, {
					headers: { 'Content-Type': 'multipart/form-data' },
				})
				this.fileName = this.uploadFile.name

				// 3. Carica headers + default mapping in parallelo
				const [headersR, mappingR] = await Promise.all([
					this.axios.get(`hrimports/${this.importId}/headers`),
					this.axios.get(`hrimports/${this.importId}/default-mapping`),
				])
				this.detectedFormat = headersR.data.format || 'tabular'
				this.formatMessage = headersR.data.message || null
				this.headers = headersR.data.headers || []
				this.sampleRows = headersR.data.sample_rows || []
				this.totalRows = headersR.data.total_rows || 0

				// Sanitizza la mappatura: tieni solo quelle che esistono nel file
				const defaultMap = mappingR.data.mapping
				const cleanMap = {}
				for (const [field, header] of Object.entries(defaultMap)) {
					if (this.headers.includes(header)) cleanMap[field] = header
				}
				this.mapping = cleanMap
				this.targetFields = mappingR.data.target_fields

				// 4. Se contracts, prefetch i CCNL
				if (this.kind === 'contracts') {
					const ccnlR = await this.axios.get('hrccnls')
					this.ccnls = ccnlR.data.data
				}

				// 5. Se il formato non e' tabellare (es. multi-sheet bilancio
				// DiC), la mappatura colonne non serve: vai direttamente al
				// dry-run.
				if (this.detectedFormat !== 'tabular') {
					await this.runDryRun()
				} else {
					this.step = 2
				}
			} catch (e) {
				this.handleError(e, 'Errore caricamento file')
			} finally {
				this.loadingUpload = false
			}
		},

		async runDryRun() {
			this.loadingDryRun = true
			try {
				const r = await this.axios.post(`hrimports/${this.importId}/dry-run`, {
					column_mapping: this.mapping,
				})
				this.importModel = r.data.model
				this.step = 3
			} catch (e) {
				this.handleError(e, 'Errore esecuzione anteprima')
			} finally {
				this.loadingDryRun = false
			}
		},

		async runCommit() {
			this.loadingCommit = true
			try {
				const r = await this.axios.post(`hrimports/${this.importId}/commit`)
				this.importModel = r.data.model
				this.step = 4
				this.loadHistory()
			} catch (e) {
				this.handleError(e, 'Errore applicazione')
			} finally {
				this.loadingCommit = false
			}
		},

		/**
		 * Risolve manualmente un alias CCNL: se freeText=true, lascia ccnl_id
		 * a null; altrimenti applica il selectedCcnlId scelto.
		 */
		async resolveCcnl(alias, freeText) {
			try {
				const payload = { alias_text: alias.text }
				if (!freeText) payload.ccnl_id = alias.selectedCcnlId
				const r = await this.axios.post(`hrimports/${this.importId}/ccnl-resolve`, payload)
				this.importModel = r.data.model
			} catch (e) {
				this.handleError(e, 'Errore risoluzione CCNL')
			}
		},

		openCreateCcnlDialog(alias) {
			this.newCcnl = { name: alias.text, code: '', sector: '' }
			this.newCcnlPendingAlias = alias
			this.newCcnlDialog = true
		},

		async confirmCreateCcnl() {
			try {
				const payload = {
					alias_text: this.newCcnlPendingAlias.text,
					create_new: { ...this.newCcnl },
				}
				const r = await this.axios.post(`hrimports/${this.importId}/ccnl-resolve`, payload)
				this.importModel = r.data.model
				// Refresh ccnl list
				const ccnlR = await this.axios.get('hrccnls')
				this.ccnls = ccnlR.data.data
				this.newCcnlDialog = false
			} catch (e) {
				this.handleError(e, 'Errore creazione CCNL')
			}
		},

		async loadHistory() {
			this.loadingHistory = true
			try {
				const r = await this.axios.post('hrimports-filters', {})
				this.history = r.data.data
			} catch (e) {
				// silenzioso: lo storico non è critico
			} finally {
				this.loadingHistory = false
				// Chiudi il progress globale (router beforeEach lo apre ad ogni
				// navigazione; ogni pagina e' responsabile di chiuderlo).
				this.$store.commit('endProgress')
			}
		},

		resetWizard() {
			this.step = 1
			this.kind = null
			this.uploadFile = null
			this.fileName = null
			this.importId = null
			this.importModel = {}
			this.headers = []
			this.sampleRows = []
			this.totalRows = 0
			this.targetFields = []
			this.mapping = {}
			this.detectedFormat = 'tabular'
			this.formatMessage = null
		},

		handleError(e, fallback) {
			const msg = (e.response && e.response.data && e.response.data.message) || fallback
			if (this.$root && this.$root.$emit) this.$root.$emit('snackbar', { text: msg, color: 'error' })
			else alert(msg)
		},
	},
}
</script>
